import {
  AI_BG_NEW,
  Context_Aware_Icon,
  Context_Awareness_Icon,
  Our_Product_Bg,
  Pulse_For_Img,
  Pulse_One_Multiple_Img,
  Pulse_Product_img,
  SDK_icon,
} from "../../assets";
import { Helmet, HelmetData } from "react-helmet-async";
import { useModal } from "../../utils/demoProvider";
import { Link } from "react-router-dom";
import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import {
  MOTION_OK,
  revealTrigger,
  riseFrom,
  maskedLineReveal,
} from "../../utils/animations";

const why = [
  {
    icon: SDK_icon,
    title: "Instrument with lightweight SDKs.",
    text: "Drop in Pulse SDKs for web, mobile, or server-side. Capture behavioural events, page views, interactions, custom events, and user properties.",
  },
  {
    icon: Context_Awareness_Icon,
    title: "Validate and enrich in real time",
    text: "Pulse validates incoming events against your schema, enriches them with session context, and structures everything into a high-fidelity format optimized for analytics and machine learning.",
  },
  {
    icon: Context_Aware_Icon,
    title: "Deliver to any destination",
    text: "Feed events directly into Data Assistant, your own analytics stack, third-party AI agents, or any downstream system via the Agentic Context API.",
  },
];

export default function Pulse() {
  const helmetData = new HelmetData({});
  const { openModal } = useModal();
  const pageRef = useRef(null);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(MOTION_OK, () => {
        maskedLineReveal(pageRef.current.querySelector(".pulse-heading"), {
          scrollTrigger: revealTrigger(".pulse-heading"),
        });
        riseFrom(".pulse-hero-item", {
          delay: 0.2,
          stagger: 0.12,
          scrollTrigger: revealTrigger(".pulse-hero-item", "top 85%"),
        });
        riseFrom(".pulse-hero-media", {
          y: 40,
          duration: 1.1,
          delay: 0.3,
          scrollTrigger: revealTrigger(".pulse-hero-media", "top 85%"),
        });
        riseFrom(".pulse-usecases-heading", {
          scrollTrigger: revealTrigger(".pulse-usecases-heading"),
        });
        riseFrom(".pulse-usecase-1 > *", {
          y: 40,
          duration: 1,
          stagger: 0.15,
          scrollTrigger: revealTrigger(".pulse-usecase-1", "top 75%"),
        });
        riseFrom(".pulse-usecase-2 > *", {
          y: 40,
          duration: 1,
          stagger: 0.15,
          scrollTrigger: revealTrigger(".pulse-usecase-2", "top 75%"),
        });
        riseFrom(".pulse-how-head > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".pulse-how-head"),
        });
        riseFrom(".pulse-how-card", {
          y: 48,
          duration: 1,
          stagger: 0.12,
          scrollTrigger: revealTrigger(".pulse-how-grid", "top 75%"),
        });
        riseFrom(".pulse-cta-card > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".pulse-cta-card"),
        });
      });
      return () => mm.revert();
    },
    { scope: pageRef }
  );

  return (
    <>
      <Helmet helmetData={helmetData}>
        <title>
          Pulse — Behavioural &amp; Event Data for AI Context
        </title>
        <link rel="canonical" href="https://analyticsintelligence.com/pulse" />
        <meta
          name="description"
          content="Pulse by Analytics Intelligence captures every user interaction across web, mobile, and server, then delivers validated, real-time context to your analytics engine, AI models, and agentic applications."
        />
        <meta
          name="keywords"
          content="Pulse, behavioural data collection, event tracking, real-time context layer, agentic context API, analytics data pipeline, web mobile server SDK, AI data enrichment, session context, Analytics Intelligence"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Analytics Intelligence Ltd" />
        <meta
          property="og:title"
          content="Pulse — Behavioural & Event Data for AI Context"
        />
        <meta
          property="og:description"
          content="Capture every user interaction across web, mobile, and server, then deliver validated, real-time context to your analytics engine, AI models, and agents."
        />
        <meta
          property="og:url"
          content="https://analyticsintelligence.com/pulse"
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://analyticsintelligence.com/favicon.png"
        />
        <meta property="og:site_name" content="Analytics Intelligence" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@_AnalyticsIntel" />
        <meta
          name="twitter:title"
          content="Pulse — Behavioural & Event Data for AI Context"
        />
        <meta
          name="twitter:description"
          content="Capture every user interaction across web, mobile, and server, then deliver validated, real-time context to your analytics engine, AI models, and agents."
        />
        <meta
          name="twitter:image"
          content="https://analyticsintelligence.com/favicon.png"
        />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Pulse — Behavioural & Event Data Collection",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web, Mobile, Server",
            "provider": {
              "@type": "Organization",
              "name": "Analytics Intelligence Ltd",
              "url": "https://analyticsintelligence.com"
            },
            "description": "Pulse is a behavioural and event-based data collection system by Analytics Intelligence that captures user interactions across web, mobile, and server, validates and enriches them in real time, and delivers context to analytics engines, AI models, and agentic applications via the Agentic Context API.",
            "url": "https://analyticsintelligence.com/pulse",
            "featureList": ["Lightweight web, mobile, and server SDKs", "Real-time validation and enrichment", "Agentic Context API", "Feeds analytics engines and AI agents"]
          }
        `}</script>
      </Helmet>
      <main ref={pageRef}>
        <section
          style={{
            backgroundImage: `url("${AI_BG_NEW}")`,
            backgroundPosition: "center",
            backgroundSize: "cover",
          }}
          className="min-h-[100vh] flex items-center"
        >
          <div className="container mx-auto w-full h-full flex flex-col md:flex-row justify-between items-center gap-16  pt-[126px] md:pt-[96px] py-[96px]">
            <div className="flex basis-2/4 flex-col flex-wrap gap-[16px] items-center md:items-start">
              <div className="space-y-4 flex flex-col items-center md:items-start">
                <span className="pulse-hero-item text-sm md:text-base bg-[#EFF0F71F] px-3 p-[6px] rounded-[100px] text-white">
                  Pulse
                </span>
                <h2
                  className="pulse-heading text-[38px] md:text-[48px] text-center md:text-left font-extrabold leading-[1.2] md:leading-[54px] text-white"
                  dangerouslySetInnerHTML={{
                    __html: "Give your AI the context it needs",
                  }}
                />
                <p className="pulse-hero-item text-sm md:text-lg text-center md:text-left font-medium text-[#E3E3E3] leading-5">
                  Pulse is a behavioural and event-based data collection
                  system that captures every user interaction across web,
                  mobile, and server, then delivers validated, real-time context
                  directly to your analytics engine, AI models, and agentic
                  applications.
                </p>
              </div>

              <div className="pulse-hero-item flex flex-row justify-center md:justify-start gap-3 sm:gap-4 w-full">
                <button
                  onClick={openModal}
                  className="text-sm md:text-base font-semibold py-3 min-w-[170px] text-center btn-sweep btn-sweep-blue text-white rounded-[100px] gap-2"
                >
                  Get Started
                </button>
              </div>
            </div>
            <div className="basis-2/4 flex justify-end">
              <img
                className="pulse-hero-media w-full"
                src={Pulse_Product_img}
                alt="illustration"
              />
            </div>
          </div>
        </section>

        <section
          className="bg-[#010127] relative flex flex-col pb-[96px]"
          id="use-cases"
        >
          <div className="w-[150px] lg:w-[400px] h-[200px] lg:h-[400px] bg-[#0073FF]/40 blur-[150px] absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]"></div>
          <h2 className="pulse-usecases-heading text-white font-extrabold text-[24px] md:text-[32px] leading-[1.2] text-center mb-11">
            One product. <br className="sm:hidden" /> Two transformative use
            cases.
          </h2>
          <div className="pulse-usecase-1 container flex flex-col-reverse md:flex-row items-center gap-8 md:gap-4 lg:gap-2 xl:gap-0 z-[100]">
            <div className="flex-1 basis-2/4 flex items-center justify-center">
              <img
                src={Pulse_For_Img}
                alt="Data Structures"
                className="w-full md:max-w-[430px] h-auto"
              />
            </div>

            <div className="flex-1 basis-2/4 space-y-4">
              <h2 class="text-2xl md:text-[32px] font-extrabold text-white text-left leading-[1.2] md:leading-[40px]">
                For Analytics Teams: Capture everything. Process instantly
              </h2>
              <p className="text-white text-sm md:text-base">
                Pulse is purpose-built to feed the Analytics Engine and AI
                Data Layer. No transformation, no mapping, no ETL gymnastics
                between collection and processing. Events flow from
                instrumentation to insight in a single, optimised pipeline.
              </p>
            </div>
          </div>

          <div
            className="pulse-usecase-2 container flex flex-col-reverse md:flex-row-reverse items-center mt-11 md:mt-20 gap-8 md:gap-4 lg:gap-2 xl:gap-0"
            style={{ zIndex: 100 }}
          >
            <div className="flex-1 basis-2/4 flex items-center justify-center">
              <img
                src={Pulse_One_Multiple_Img}
                alt="Data Structures"
                className="w-full md:max-w-[516px] h-auto"
              />
            </div>

            <div className="flex-1 basis-2/4 space-y-4">
              <h2 class="text-2xl md:text-[32px] font-extrabold text-white text-left leading-[1.2] md:leading-[40px]">
                For Teams Building AI Agents & Copilots: The context layer for
                the agentic era
              </h2>
              <p className="text-white text-sm md:text-base">
                Every AI agent and copilot in your stack is only as good as the
                context it receives. Pulse delivers validated, real-time
                behavioural signals, what the user was doing, where they were in
                a journey, and what preceded the interaction.
              </p>
            </div>
          </div>
        </section>

        <section className="bg-[#EDF1FF] py-[48px]">
          <div className="container space-y-12">
            <div className="pulse-how-head">
              <h2 className="text-[#020128] font-extrabold text-[24px] md:text-[32px] text-center">
                How It Works
              </h2>
              <p className="text-[#0073FF] text-lg md:text-2xl text-center font-bold">
                Instrument once. Feed everything.
              </p>
            </div>

            <div className="pulse-how-grid grid grid-cols-12 gap-8">
              {why.map((item, idx) => (
                <div
                  key={idx}
                  className="pulse-how-card group col-span-12 md:col-span-6 lg:col-span-4 bg-white hover:bg-poppingBlue flex flex-col items-center rounded-xl px-3 py-6 gap-4 transition-colors duration-700"
                >
                  <img
                    src={item.icon}
                    alt={`${item.title} illustration`}
                    className="w-16 h-16 transition-all duration-700 group-hover:brightness-0 group-hover:invert"
                  />
                  <div className="space-y-2 text-center">
                    <h5 className="text-lg md:text-xl font-bold text-[#020128] transition-colors duration-700 group-hover:text-white">
                      {item.title}
                    </h5>
                    <p className="text-sm md:text-base text-[#020128] transition-colors duration-700 group-hover:text-white">
                      {item.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section
          className="relative pt-[116px] pb-10"
          style={{
            backgroundImage: `url("${Our_Product_Bg}")`,
            backgroundPosition: "center",
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
          }}
        >
          <div className="container">
            <div className="pulse-cta-card bg-[#1E1E4080] backdrop-blur-[20px] flex flex-col items-center gap-6 px-4 md:px-8 py-8 sm:py-12 md:py-14 lg:py-16 xl:py-20 rounded-3xl">
              <div className="">
                <h2 className="text-[32px] md:text-[44px] text-center font-extrabold text-white leading-[1.3] max-w-[890px] md:leading-[50px]">
                  Pulse is included at no additional cost when bundled with Data
                  Assistant
                </h2>
              </div>

              <div className="flex flex-row gap-y-3 sm:gap-y-0 gap-x-4 md:gap-x-8 w-full justify-center">
                <Link to="/contact">
                  <button className="text-sm md:text-base font-semibold py-3 px-4 md:px-6 min-w-[120px] sm:min-w-[170px] text-center btn-sweep btn-sweep-white text-poppingBlue rounded-[100px] gap-2">
                    Talk to sales
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
